/**
 * Geëmailleerd glas — RAL-logica (Timmers-prijslijst p.21).
 *
 * De klant kiest een basis (Blank / Extra helder) + een willekeurige RAL-kleur. Of die
 * RAL "standaard" of "niet-standaard" is volgt uit een vaste lijst van 12 codes; dat
 * bepaalt zowel de prijsvariant als de eenmalige instelkost (€125 standaard / €135
 * niet-standaard, 1× per kleur per order).
 *
 * Eén bron, gedeeld door client (configurator + winkelwagen) en server (herprijzing).
 */

/** Subcategorie-naam zoals in glass-manual.json (de variant-subcategory). */
export const GEEMAILLEERD_SUBCATEGORY = "Geëmailleerd glas";

/** Basis-uitvoeringen (glas), los van de RAL-kleur. */
export const EMAIL_BASES = ["Blank", "Extra helder"] as const;
export type EmailBase = (typeof EMAIL_BASES)[number];

/** De 12 standaard RAL-kleuren voor geëmailleerd glas (prijslijst p.21). */
export const EMAIL_STANDARD_RAL = [
  "7001", "7015", "7016", "7021", "7022", "7031", "7036", "7040", "7042", "7046", "9005", "9016",
] as const;

/** Eenmalige instelkost (centen excl. btw), 1× per kleur per order. */
export const EMAIL_INSTELKOST_STANDAARD_CENTS = 12500;
export const EMAIL_INSTELKOST_NIETSTANDAARD_CENTS = 13500;

const normRal = (code: string): string => code.replace(/\D/g, "");

/** Is deze RAL-code een standaardkleur voor geëmailleerd glas? */
export function isStandardEmailRal(code: string): boolean {
  const n = normRal(code);
  return n.length > 0 && (EMAIL_STANDARD_RAL as readonly string[]).map(normRal).includes(n);
}

/** Eenmalige instelkost (centen) voor de gekozen RAL-kleur. */
export function emailInstelkostCents(code: string): number {
  return isStandardEmailRal(code) ? EMAIL_INSTELKOST_STANDAARD_CENTS : EMAIL_INSTELKOST_NIETSTANDAARD_CENTS;
}

/** Basis + RAL → de variant-`color`-string (zoals in glass-manual.json). */
export function emailVariantColor(base: EmailBase, code: string): string {
  return `${base} ${isStandardEmailRal(code) ? "standaard" : "niet standaard"} RAL`;
}

/** Hoort deze variant-`color` bij geëmailleerd glas (een van de 4 RAL-klassen)? */
export function isEmailVariantColor(color: string | undefined): boolean {
  return !!color && /^(Blank|Extra helder) (niet )?standaard RAL$/.test(color);
}

/** Basis uit een geëmailleerd-variant-`color` ("Extra helder standaard RAL" → "Extra helder"). */
export function emailBaseOf(color: string): EmailBase {
  return color.startsWith("Extra helder") ? "Extra helder" : "Blank";
}

/** Is de variant-`color` de standaard-RAL-klasse (true) of niet-standaard (false)? */
export function emailColorIsStandard(color: string): boolean {
  return !/niet standaard/.test(color);
}

/**
 * BiColour geëmailleerd glas: een andere RAL op de binnen- dan de buitenzijde. De prijs volgt
 * de DUURSTE van de twee: alleen als béíde RAL-codes standaard zijn geldt de standaard-klasse,
 * anders de (duurdere) niet-standaard-klasse. Zo komt de prijs — net als bij één kleur — uit de
 * bestaande variant-tarieven (de API).
 */
export function emailTierIsStandard(ralA: string, ralB?: string): boolean {
  return isStandardEmailRal(ralA) && (!ralB || !ralB.trim() || isStandardEmailRal(ralB));
}

/** Variant-`color` voor een (bi)kleur-keuze: basis + de duurste RAL-klasse van beide codes. */
export function emailVariantColorBi(base: EmailBase, ralA: string, ralB?: string): string {
  return `${base} ${emailTierIsStandard(ralA, ralB) ? "standaard" : "niet standaard"} RAL`;
}

/** Eenmalige instelkost voor een (bi)kleur-keuze: 1× de duurste klasse (niet 2×). */
export function emailInstelkostBiCents(ralA: string, ralB?: string): number {
  return emailTierIsStandard(ralA, ralB) ? EMAIL_INSTELKOST_STANDAARD_CENTS : EMAIL_INSTELKOST_NIETSTANDAARD_CENTS;
}
